import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ArrowLeftRight } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getCenterOlPayment } from '../../api/center-ol-payments/get-center-ol-payment'
import { updateCenterOlPayment } from '../../api/center-ol-payments/update-center-ol-payment'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { CenterOlPaymentForm } from '../../components/pages/center-ol-payments/center-ol-payment-form'
import { Paths } from '../../lib/constants/path'
import useTranslate from '../../lib/hooks/use-translate'
import {
  CenterOlPaymentStatusEnum,
  centerOlPaymentStatusEnumMapping
} from '../../lib/types/enums/center-ol-payment/center-ol-payment-status.enum'
import { CenterOlPayment } from '../../lib/types/models/center-ol-payment/center-ol-payment'
import { CenterOlPaymentInput } from '../../lib/types/models/center-ol-payment/center-ol-payment-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const mapCenterOlPaymentToInput = (account: CenterOlPayment): CenterOlPaymentInput => ({
  amount: account.amount,
  currency: account.currency,
  additionalLabel: account.additionalLabel,
  centerId: account.centerId,
  dateOfPayment: account.dateOfPayment,
  label: account.label,
  lecBankAccountId: account.lecBankAccountId,
  olId: account.olId,
  type: account.type,
  countryId: account.countryId,
  files: account.files,
  olBankAccountId: account.olBankAccountId
})

export function CenterOlPaymentsUpdatePage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslate()
  const queryClient = useQueryClient()
  const { navigate } = useNavigator()

  if (!id || isNaN(parseInt(id))) return <ItemNotFound title="Identifiant de virement invalide" />

  const { data: centerOlPayment, isLoading } = useQuery({
    queryKey: ['center-ol-payment', id],
    queryFn: () => getCenterOlPayment(parseInt(id))
  })

  const { mutateAsync: updateCenterOlPaymentMutation } = useMutation({
    mutationFn: (values: CenterOlPaymentInput) => updateCenterOlPayment(parseInt(id), values),
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['center-ol-payment', id] })
      toast.success('Virement mis à jour avec succès')
      navigate(Paths.CENTER_OL_PAYMENTS_LIST_PAGE)
    }
  })

  if (isLoading) return <LoadingScreen />
  if (!centerOlPayment) return <ItemNotFound title="Virement non trouvé" />
  const isValidated = centerOlPayment.status === CenterOlPaymentStatusEnum.VALIDATED

  return (
    <div>
      <UserNavBar
        title={'Modifier le virement'}
        badgeTitle={t(centerOlPaymentStatusEnumMapping[centerOlPayment.status].label)}
        badgeVariant={centerOlPaymentStatusEnumMapping[centerOlPayment.status].color}
        icon={ArrowLeftRight}
      />
      <div className={'h-4'} />
      <Box className={'flex flex-col gap-4'}>
        <CenterOlPaymentForm
          mode={isValidated ? 'view' : 'default'}
          periods={centerOlPayment.periods}
          defaultValues={mapCenterOlPaymentToInput(centerOlPayment)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => navigate(Paths.CENTER_OL_PAYMENTS_LIST_PAGE),
            onUpdate: isValidated
              ? undefined
              : async (values) => {
                  await updateCenterOlPaymentMutation(values)
                }
          })}
        />
      </Box>
    </div>
  )
}
